import clsx from 'clsx'

interface GuestSelectorProps {
  value: number
  onChange: (nextValue: number) => void
  minGuests?: number
  maxGuests: number
  remainingCapacity?: number | null
  pricePerGuest: number
  error?: string
}

const GuestSelector = ({
  value,
  onChange,
  minGuests = 1,
  maxGuests,
  remainingCapacity,
  pricePerGuest,
  error,
}: GuestSelectorProps) => {
  const effectiveMax = remainingCapacity ? Math.min(maxGuests, remainingCapacity) : maxGuests
  const decreaseDisabled = value <= minGuests
  const increaseDisabled = value >= effectiveMax
  const totalPrice = value * pricePerGuest

  const handleDecrease = () => {
    if (decreaseDisabled) return
    onChange(value - 1)
  }

  const handleIncrease = () => {
    if (increaseDisabled) return
    onChange(value + 1)
  }

  return (
    <section aria-labelledby="guest-count-heading" className="superz-widget-panel p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Your Entourage</p>
          <h2 id="guest-count-heading" className="font-display text-3xl text-widget-accent">
            Guests
          </h2>
        </div>
        <span className="rounded-full bg-widget-primary/15 px-3 py-1 text-xs text-widget-accent/70">
          {pricePerGuest.toLocaleString('hu-HU')} Ft / guest
        </span>
      </header>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleDecrease}
            disabled={decreaseDisabled}
            className={clsx(
              'flex h-12 w-12 items-center justify-center rounded-full border text-2xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary',
              decreaseDisabled
                ? 'border-widget-primary/30 bg-widget-primary/10 text-widget-accent/30'
                : 'border-widget-secondary bg-widget-secondary text-widget-primary shadow-[0_4px_12px_rgba(var(--widget-secondary-rgb),0.3)] hover:shadow-[0_8px_20px_rgba(var(--widget-secondary-rgb),0.4)] hover:scale-102 active:scale-98',
            )}
            aria-label="Decrease guest count"
          >
            –
          </button>

          <span className="text-4xl font-bold text-widget-accent">{value}</span>

          <button
            type="button"
            onClick={handleIncrease}
            disabled={increaseDisabled}
            className={clsx(
              'flex h-12 w-12 items-center justify-center rounded-full border text-2xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary',
              increaseDisabled
                ? 'border-widget-primary/30 bg-widget-primary/10 text-widget-accent/30'
                : 'border-widget-secondary bg-widget-secondary text-widget-primary shadow-[0_4px_12px_rgba(var(--widget-secondary-rgb),0.3)] hover:shadow-[0_8px_20px_rgba(var(--widget-secondary-rgb),0.4)] hover:scale-102 active:scale-98',
            )}
            aria-label="Increase guest count"
          >
            +
          </button>
        </div>

        <div className="text-right">
          <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Total Investment</p>
          <p className="font-display text-3xl text-widget-secondary">{totalPrice.toLocaleString('hu-HU')} Ft</p>
        </div>
      </div>

      <footer className="mt-4 text-xs text-widget-accent/70">
        <p>
          Maximum of {maxGuests} guests per private consultation{' '}
          {remainingCapacity !== undefined && remainingCapacity !== null && (
            <span>(currently {remainingCapacity} spots remaining)</span>
          )}
        </p>
        {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
      </footer>
    </section>
  )
}

export default GuestSelector


